import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { getRouteApi } from '@tanstack/react-router';
import { ChevronUp } from 'lucide-react';

import { useEstateOptions } from '@/api/estate-options';
import { Card } from '@/components/ui/card';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import type { Projects } from '@/features/projects/api/projects';
import { cn } from '@/lib/utils';
import { getRenderValue } from '@/utils/getEmptyVisualValue';
import { getFinalAreaRoom } from '@/utils/getFinalAreaRoom';
import { getFinalLivingArea } from '@/utils/getFinalLivingArea';
import { getFinalPrice } from '@/utils/getFinalPrice';

import type { Estates } from '../api/estates';
import type { EstateInfoProps } from './estate-info';
import { EstatesInfiniteScroll } from './estates-infinite-scroll';
import { EstatesListCard } from './estates-list-card';

type EstateListData = Pick<Estates[number], 'id' | 'name' | 'status' | 'thumbnail' | 'createdAt'> &
  Partial<Pick<Estates[number], 'creator' | 'estateDetails' | 'estateAreasRooms'>> & {
    address?: {
      address?: string | null;
      postalCode?: string | null;
      city?: string | null;
    } | null;
    prices?: Projects[number]['prices'] | Estates[number]['prices'] | null;
    areaRooms?: Projects[number]['areaRooms'] | null;
    projects?: {
      id: string;
      name: string;
    } | null;
  };

interface EstatesListProps {
  data: EstateListData[];
  filters?: object;
  filterOwn?: boolean;
  infiniteScroll?: boolean;
  showBuilding?: boolean;
  type?: 'estate' | 'project';
  btnActionRender?: (props: EstateInfoProps) => JSX.Element | null;
  route: ReturnType<typeof getRouteApi>;
}

export const EstatesList: React.FC<EstatesListProps> = ({
  data,
  filters,
  filterOwn,
  infiniteScroll = true,
  showBuilding = false,
  type = 'estate',
  btnActionRender,
  route,
}) => {
  const { t } = useTranslation();
  const { data: estateOptions } = useEstateOptions();
  const { sort } = route.useSearch();
  const navigate = route.useNavigate();

  const [sortField, setSortField] = useState<
    'name' | 'rooms' | 'area' | 'price' | 'createdAt' | 'updatedAt' | 'creator' | undefined
  >(undefined);

  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | undefined>(undefined);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc'); // toggle to desc
      } else if (sortDirection === 'desc') {
        // reset both
        setSortField(undefined);
        setSortDirection(undefined);
      } else {
        setSortDirection('asc'); // initial click
      }
    } else {
      setSortField(field);
      setSortDirection('asc'); // new field always asc
    }
  };

  // run navigate whenever sortField or sortDirection changes
  useEffect(() => {
    navigate({
      search: (prev) => {
        if (!sortField || !sortDirection) {
          // biome-ignore lint/correctness/noUnusedVariables: remove sort if either field or direction is undefined
          const { sort, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          sort: {
            by: sortField,
            order: sortDirection,
          },
        };
      },
    });
  }, [sortField, sortDirection, navigate]);

  const SortableHeader: React.FC<{
    field: 'name' | 'rooms' | 'area' | 'price' | 'createdAt' | 'updatedAt' | 'creator';
    children: React.ReactNode;
    className?: string;
  }> = ({ field, children, className = '' }) => {
    const isActive = sort?.by === field;
    const isDesc = isActive && sort?.order === 'desc';

    return (
      <button
        type="button"
        onClick={() => handleSort(field)}
        className={cn(
          'flex items-center gap-1 text-md font-normal leading-[28px] text-primary-dark transition-colors hover:text-primary',
          className
        )}
      >
        <span>{children}</span>
        {isActive ? (
          <div className="flex flex-col">
            <ChevronUp size={12} className={cn('transition-colors', isDesc ? 'rotate-180' : '')} />
          </div>
        ) : null}
      </button>
    );
  };

  return (
    <Card className="px-3 py-3.5 sm:px-6">
      <div className="mb-3 flex items-center gap-x-2">
        <SortableHeader field="name" className="ml-4 min-w-[250px] flex-1">
          {t('title')}
        </SortableHeader>
        <SortableHeader field="price" className="w-[120px]">
          {t('price')}
        </SortableHeader>
        <SortableHeader field="area" className="w-[100px]">
          {t('area')}
        </SortableHeader>
        <SortableHeader field="rooms" className="w-[60px]">
          {t('rooms')}
        </SortableHeader>
        {showBuilding ? (
          <div className="w-[80px] flex-1 text-md font-normal leading-[28px] text-primary-dark">
            {t('building')}
          </div>
        ) : null}
        <SortableHeader field="creator" className="w-[120px]">
          {t('author')}
        </SortableHeader>
        <SortableHeader field="createdAt" className="w-[90px]">
          {t('created')}
        </SortableHeader>
        {btnActionRender ? (
          <div className="w-[80px] flex-1 text-md font-normal text-primary-dark"></div>
        ) : null}
      </div>

      <div className="flex flex-col gap-y-[13px]">
        {data.map((estate) => {
          const price = getFinalPrice(estate);
          const roomsCount = getFinalAreaRoom(estate);
          const livingArea = getFinalLivingArea(estate);

          return (
            <EstatesListCard
              key={estate.id}
              propertyLevel={
                'estateDetails' in estate ? PROPERTY_LEVELS.OBJECT : PROPERTY_LEVELS.PROJECT
              }
              status={estate.status}
              id={estate.id}
              address={estate.address}
              marketType={estate.estateDetails?.marketType}
              propertyType={estate.estateDetails?.propertyType}
              usageType={estate.estateDetails?.usageType}
              livingArea={livingArea}
              externalId={estate.estateDetails?.externalId}
              building={estate.estateDetails?.building}
              name={estate.name}
              price={price}
              roomsCount={roomsCount}
              createdAt={estate.createdAt}
              creator={`${getRenderValue(estate.creator?.firstName)} ${getRenderValue(estate.creator?.lastName)}`}
              thumbnailId={estate.thumbnail?.mediaId || ''}
              btnActionRender={btnActionRender}
              estateOptions={estateOptions}
              showBuilding={showBuilding}
              projects={estate?.projects}
            />
          );
        })}
        {infiniteScroll ? (
          <EstatesInfiniteScroll
            Card={EstatesListCard}
            filters={filters}
            filterOwn={filterOwn}
            sort={sort}
            type={type}
            cardProps={{
              showBuilding,
              btnActionRender,
            }}
          />
        ) : null}
      </div>
    </Card>
  );
};
